import * as React from "react";
import { createPortal } from "react-dom";
import { cn } from "./ui/index.ts";
import { I } from "./icons/icons.tsx";

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

const triggerClass =
  "inline-flex h-9 w-full items-center gap-2 rounded-[var(--sf-radius)] border border-[var(--sf-border-strong)] bg-[var(--sf-surface)] pr-8 pl-2.5 text-left text-[13.5px] font-[inherit] text-[var(--sf-text)] outline-none focus:border-[var(--sf-primary)] focus:shadow-[0_0_0_3px_color-mix(in_srgb,var(--sf-primary)_12%,transparent)]";

export function Select({
  options,
  value,
  onChange,
  placeholder = "Sélectionner…",
  className,
  disabled,
}: {
  options: SelectOption[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
}) {
  const [open, setOpen] = React.useState(false);
  const [highlight, setHighlight] = React.useState(0);
  const [rect, setRect] = React.useState<{
    top: number;
    left: number;
    width: number;
  } | null>(null);
  const rootRef = React.useRef<HTMLDivElement>(null);
  const triggerRef = React.useRef<HTMLButtonElement>(null);
  const listRef = React.useRef<HTMLUListElement>(null);
  const listId = React.useId();

  const selected = options.find((o) => o.value === value) ?? null;

  React.useEffect(() => {
    if (!open) return;
    const idx = options.findIndex((o) => o.value === value);
    setHighlight(idx >= 0 ? idx : 0);
  }, [open, options, value]);

  React.useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      const t = e.target as Node;
      if (rootRef.current?.contains(t)) return;
      if (listRef.current?.contains(t)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  React.useEffect(() => {
    if (!open) return;
    const update = () => {
      const el = triggerRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      setRect({ top: r.bottom + 4, left: r.left, width: r.width });
    };
    update();
    window.addEventListener("resize", update);
    window.addEventListener("scroll", update, true);
    return () => {
      window.removeEventListener("resize", update);
      window.removeEventListener("scroll", update, true);
    };
  }, [open]);

  React.useEffect(() => {
    if (!open) return;
    const el = listRef.current?.querySelector<HTMLElement>(
      `[data-option-index="${highlight}"]`,
    );
    el?.scrollIntoView({ block: "nearest" });
  }, [open, highlight]);

  const pick = (opt: SelectOption) => {
    if (opt.disabled) return;
    onChange(opt.value);
    setOpen(false);
    triggerRef.current?.focus();
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      if (!open) {
        setOpen(true);
        return;
      }
      setHighlight((h) => {
        const next = Math.min(h + 1, options.length - 1);
        return options[next]?.disabled ? h : next;
      });
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      if (!open) {
        setOpen(true);
        return;
      }
      setHighlight((h) => {
        const next = Math.max(h - 1, 0);
        return options[next]?.disabled ? h : next;
      });
    } else if ((e.key === "Enter" || e.key === " ") && open) {
      e.preventDefault();
      const opt = options[highlight];
      if (opt) pick(opt);
    } else if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      setOpen(true);
    } else if (e.key === "Escape") {
      e.preventDefault();
      setOpen(false);
    }
  };

  return (
    <div ref={rootRef} className={cn("relative", className)}>
      <button
        ref={triggerRef}
        type="button"
        role="combobox"
        aria-expanded={open}
        aria-controls={listId}
        aria-haspopup="listbox"
        onClick={() => !disabled && setOpen((o) => !o)}
        onKeyDown={onKeyDown}
        onBlur={(e) => {
          if (listRef.current?.contains(e.relatedTarget as Node)) return;
          setOpen(false);
        }}
        disabled={disabled}
        className={cn(
          triggerClass,
          disabled && "cursor-not-allowed opacity-60",
        )}
      >
        <span
          className={cn(
            "min-w-0 flex-1 truncate",
            !selected && "text-sf-text-muted",
          )}
        >
          {selected?.label ?? placeholder}
        </span>
        <span className="pointer-events-none absolute top-1/2 right-2.5 -translate-y-1/2 text-sf-text-muted">
          <I.ChevronDown
            size={14}
            className={cn(
              "transition-transform duration-150",
              open && "rotate-180",
            )}
          />
        </span>
      </button>

      {open &&
        rect &&
        options.length > 0 &&
        createPortal(
          <ul
            ref={listRef}
            id={listId}
            role="listbox"
            style={{
              position: "fixed",
              top: rect.top,
              left: rect.left,
              width: rect.width,
              zIndex: 600,
            }}
            className="max-h-[min(320px,48vh)] overflow-y-auto rounded-[var(--sf-radius-lg)] border border-sf-border bg-white py-1 shadow-[var(--sf-shadow-lg)]"
          >
            {options.map((opt, i) => (
              <li
                key={opt.value}
                role="option"
                aria-selected={opt.value === value}
              >
                <button
                  type="button"
                  tabIndex={-1}
                  data-option-index={i}
                  disabled={opt.disabled}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => pick(opt)}
                  onMouseEnter={() => !opt.disabled && setHighlight(i)}
                  className={cn(
                    "flex w-full items-center gap-2 border-none px-2.5 py-2 text-left font-[inherit] text-[13px] text-sf-text transition-colors",
                    opt.disabled
                      ? "cursor-not-allowed text-sf-text-muted opacity-60"
                      : "cursor-pointer",
                    !opt.disabled && i === highlight
                      ? "bg-sf-primary-50"
                      : "bg-transparent hover:bg-gray-50",
                  )}
                >
                  <span className="min-w-0 flex-1 truncate">{opt.label}</span>
                  {opt.value === value && (
                    <I.Check size={12} className="shrink-0 text-sf-primary" />
                  )}
                </button>
              </li>
            ))}
          </ul>,
          document.body,
        )}
    </div>
  );
}
